import { Component, OnChanges, OnInit, OnDestroy, Input, Output, EventEmitter, Directive } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { resumeDataInterface } from '../../interfaces/resume';
import { ListService } from '../services/list.service';

import { TokenStorageService } from '../../services/auth-user-service/token-storage.service';

@Directive()
export class BaseListComponent implements OnChanges {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  private currentOrdering: any = {};
  private orderingCycle: Array<string> = ['', 'ASC', 'DESC'];
  private unsubscriber : Subject<void> = new Subject<void>();

  public firstRegisterShown: number;

  public lastRegisterShown: number;
  public rowsPerPageOptions: Array<number> = [5, 10, 20, 50, 100, 200, 500, 1000, 10000];
  public showFilters: boolean = true;

  public orderingParameters: any;
  private routeParamsSubs: any;
  public rol: any = 0;

  public createProposal(){this.router.navigate(['proposal', 'new']);}
  public createPreProposal(){this.router.navigate(['proposalpe', 'new']);}

  private computeDisplayedRows(): void {
    this.firstRegisterShown = 1 + this.listService.currentPage[this.routeKey] * this.listService.rowsPerPage;
    this.lastRegisterShown = Math.min(this.firstRegisterShown + this.listService.rowsPerPage - 1, this.elementList.registers);
  }

  private requestRowsToParent(): void {
    this.requestRows.emit({
      skip : this.firstRegisterShown - 1,
      limit: this.listService.rowsPerPage,
      conds: this.projectCnds[0],
      filters: this.listService.activeFilters,
      ordering: this.currentOrdering
    });
  }

  public export(){
    this.requestRows.emit({
      skip : this.firstRegisterShown - 1,
      limit: this.listService.rowsPerPage,
      filters: this.listService.activeFilters,
      ordering: this.currentOrdering,
      export: true
    });
  }

  public computeActiveFilters(){
    this.firstRegisterShown = 1 + this.listService.currentPage[this.routeKey] * this.listService.rowsPerPage;
    this.requestRowsToParent();
  }

  public cycleOrdering(key) {
    let i = this.orderingParameters[key]?this.orderingParameters[key]:0;

    this.orderingParameters = {};
    this.orderingParameters[key] = (1 + i) % this.orderingCycle.length;

    this.currentOrdering = {};
    this.currentOrdering[key] = this.orderingCycle[this.orderingParameters[key]];
    this.requestRowsToParent();
  }

  public requestPrevious(): void {
    this.listService.currentPage[this.routeKey] = Math.max(0, this.listService.currentPage[this.routeKey] - 1);
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public requestNext(): void {
    this.listService.currentPage[this.routeKey] = Math.min(Math.floor(this.elementList.registers / this.listService.rowsPerPage), this.listService.currentPage[this.routeKey] + 1);
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public gotoPage(page: number): void {
    this.listService.currentPage[this.routeKey] = page;
    this.computeDisplayedRows();
    this.requestRowsToParent();
  }

  public navigateToProject(project) {
    this.router.navigate([this.routeKey, project.codi]);
  }

  public setRowPerPage(rowsPerPage: number) {
    this.listService.rowsPerPage = rowsPerPage;
    this.requestRowsToParent();
  }

  public toggleFiltersVisibility() {
    this.showFilters = !this.showFilters;
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, public listService: ListService, public tokenStorage: TokenStorageService, route: ActivatedRoute, private router: Router, public tableFields, public routeKey: string,
              public projectCnds: Array<any>, public projectFilters: Array<any>,public ordreCamps: Array<any>) {
    if(this.listService.activeFilters)projectFilters = this.listService.activeFilters;//Si ja hi ha filtre desat, s'activa
    this.rol = this.tokenStorage.getRol();
    this.orderingParameters = this.tableFields.reduce((acum, curr) => {
      acum[curr[0]] = 0;
      return acum;
    }, {});
    for(var i=0;i<ordreCamps.length;i++){this.orderingParameters[ordreCamps[i]] = 1;this.cycleOrdering(ordreCamps[i]);}
    this.routeParamsSubs = route.params.subscribe(params => {
      if(params.user)projectFilters[1].inputs[0].values.find(item => item.name == 'dataTecnic').value = params.user;
    });
  }

  ngOnDestroy() { this.routeParamsSubs.unsubscribe(); this.unsubscriber.next(); this.unsubscriber.complete(); }
  ngOnChanges(changes: any): void {
    const newElementList = changes.elementList.currentValue;
    if (newElementList)this.computeDisplayedRows();
  }

  protected watchLangChange(rebuild: () => void) {
    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => rebuild());
  }
}

@Component({
  selector: 'mcrit-gestio-projects-list',
  templateUrl: 'projects-list.component.html',
  styleUrls: ['projects-list.component.css']
})
export class ProjectsListComponent extends BaseListComponent implements OnInit {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  public title: string;
  public isDate(field: string): boolean {
    return (field === 'data_aprovacio' || field === 'data_finalitzacio');
  }

  private static buildTableFields(translate: TranslateService): Array<[string, string, number]> {
    return [
      ['codi', translate.instant('projectsList.camps.codi'), 12],
      ['titol', translate.instant('projectsList.camps.titol'), 16],
      ['empresa', translate.instant('projectsList.camps.empresa'), 10],
      ['socis', translate.instant('projectsList.camps.socis'), 10],
      ['estat_projecte', translate.instant('projectsList.camps.estat'), 10],
      ['name', translate.instant('projectsList.camps.tecnic'), 8],
      ['data_aprovacio', translate.instant('projectsList.camps.aprovacio'), 8],
      ['data_finalitzacio', translate.instant('projectsList.camps.finalitzacio'), 8],
      ['ppropi', translate.instant('projectsList.camps.propiEur'), 6],
      ['pressupost_contractat', translate.instant('projectsList.camps.contractatEur'), 6],
      ['pressupost_projecte_loc', translate.instant('projectsList.camps.totalLoc'), 6]
    ];
  }

  private static buildFilters(translate: TranslateService, listService: ListService): Array<any> {
    return [
      {
        inputs: [
          {
            name: 'projectState',
            type: 'checkbox',
            values: [
              { text: translate.instant('projectsList.estatsProjecte.iniciats'), name: 'stateEnContractacio', value: false },
              { text: translate.instant('projectsList.estatsProjecte.enCurs'), name: 'stateActive', value: false },
              { text: translate.instant('projectsList.estatsProjecte.finalitzatsNoCobrats'), name: 'stateFinished', value: false },
              { text: translate.instant('projectsList.estatsProjecte.cobratsNoFinalitzats'), name: 'stateCobratNotFinished', value: false },
              { text: translate.instant('projectsList.estatsProjecte.aturats'), name: 'statePaused', value: false },
              { text: translate.instant('projectsList.estatsProjecte.tancats'), name: 'stateClosed', value: false },
              { text: translate.instant('projectsList.estatsProjecte.cancellats'), name: 'stateCancelled', value: false },
              { text: translate.instant('projectsList.estatsProjecte.destacats'), name: 'destacat', value: false },
              { text: translate.instant('project-data.badges.innovador'), name: 'innovador', value: false },
              { text: translate.instant('projectsList.filtresErrorsProjecte.imports'), icon: 'alarm_off', name: 'importsf' },
              { text: translate.instant('projectsList.filtresErrorsProjecte.dates'), icon: 'date_range', name: 'datesf' },
              { text: translate.instant('projectsList.filtresErrorsProjecte.datesCobrament'), icon: 'event', name: 'datesc' },
              { text: translate.instant('projectsList.filtresErrorsProjecte.senseFites'), icon: 'pin_drop', name: 'nofites' },
              { text: translate.instant('projectsList.filtresErrorsProjecte.errorsCertificats'), icon: 'flare', name: 'nocertif' }
            ]
          }
        ]
      },
      {
        inputs: [
          {
            name: 'text',
            type: 'text',
            values: [
              { text: translate.instant('projectsList.camps.codi'), name: 'clau', autocomplete: 'codiClau', value: listService.getSearchValues('codi') },
              { text: translate.instant('projectsList.camps.titol'), name: 'dataTitol', autocomplete: 'titol', value: listService.getSearchValues('dataTitol'), type: 'text' },
              { text: translate.instant('projectsList.campsCerca.expedient'), name: 'expedient', autocomplete: 'codiExpedient', value: listService.getSearchValues('expedient') },
              { text: translate.instant('projectsList.campsCerca.cpv'), name: 'dataCPV', autocomplete: 'cpv', value: listService.getSearchValues('dataCPV') },
              { text: translate.instant('projectsList.campsCerca.tipusEspecific'), name: 'dataTematica', autocomplete: 'tematica1', value: listService.getSearchValues('dataTematica') },
              { text: translate.instant('projectsList.campsCerca.serveis'), name: 'dataTematica2', autocomplete: 'tematica2', value: listService.getSearchValues('dataTematica2') },
              { text: translate.instant('projectsList.campsCerca.sectors'), name: 'dataTematica3', autocomplete: 'tematica3', value: listService.getSearchValues('dataTematica3') },
              { text: translate.instant('projectsList.campsCerca.sectorEspecific'), name: 'dataTematica4', autocomplete: 'tematica4', value: listService.getSearchValues('dataTematica4') },
              { text: translate.instant('projectsList.campsCerca.sistemes'), name: 'dataTematica5', autocomplete: 'tematica5', value: listService.getSearchValues('dataTematica5') },
              { text: translate.instant('projectsList.campsCerca.pais'), name: 'dataPais', autocomplete: 'paisProp', value: listService.getSearchValues('dataPais') },
              { text: translate.instant('projectsList.campsCerca.regio'), name: 'dataRegio', autocomplete: 'regioProp', value: listService.getSearchValues('dataRegio') },
              { text: translate.instant('projectsList.campsCerca.ciutat'), name: 'dataCiutat', autocomplete: 'ciutatProp', value: listService.getSearchValues('dataCiutat') },
              { text: translate.instant('projectsList.campsCerca.ambitProposta'), name: 'ambitProp', autocomplete: 'ambitProp', value: listService.getSearchValues('ambitProp') },
              { text: translate.instant('projectsList.camps.empresa'), name: 'dataEmpresa', autocomplete: 'empresa', value: listService.getSearchValues('dataEmpresa') },
              { text: translate.instant('projectsList.campsCerca.client'), name: 'dataClient', autocomplete: 'client', value: listService.getSearchValues('dataClient') },
              { text: translate.instant('projectsList.camps.socis'), name: 'dataSocis', autocomplete: 'soci', value: listService.getSearchValues('dataSocis') },
              { text: translate.instant('projectsList.camps.tecnic'), name: 'dataTecnic', autocomplete: 'tecnicMcrit', value: listService.getSearchValues('dataTecnic') },
              // NOTA: al codi original hi havia un typo "Pressupot" (faltava la "s"); es corregeix aquí.
              { text: translate.instant('projectsList.campsCerca.pressupostProjecteLoc'), name: 'pressupost_projecte_loc', autocomplete: '', value: listService.getSearchValues(''), type: 'value' },
              { text: translate.instant('projectsList.campsCerca.dataAprovacio'), name: 'data_aprovacio', autocomplete: '', value: listService.getSearchValues(''), type: 'date' },
              { text: translate.instant('projectsList.campsCerca.dataFinalitzacio'), name: 'data_finalitzacio', autocomplete: '', value: listService.getSearchValues(''), type: 'date' }
            ]
          }
        ]
      }
    ];
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, listService: ListService, tokenStorage: TokenStorageService, route: ActivatedRoute, router: Router) {
    super(translate, languageService, listService, tokenStorage, route, router,
      ProjectsListComponent.buildTableFields(translate),
      'project',
      [
        {
          cnd_dataEmpresa: ' = ' //totes les condicions van dins l'objecte, no com array!!!
        }
      ],
      ProjectsListComponent.buildFilters(translate, listService),
      ['data_aprovacio']
    );
    this.title = translate.instant('projectsList.titols.treballs');
  };

  ngOnInit() {
    this.watchLangChange(() => {
      this.title = this.translate.instant('projectsList.titols.treballs');
      this.tableFields = ProjectsListComponent.buildTableFields(this.translate);
      this.projectFilters = ProjectsListComponent.buildFilters(this.translate, this.listService);
    });
  }
}

@Component({
  selector: 'mcrit-gestio-proposals-list',
  templateUrl: 'projects-list.component.html',
  styleUrls: ['projects-list.component.css']
})
export class ProposalsListComponent extends BaseListComponent implements OnInit {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  public title: string;

  public isDate(field: string): boolean {
    return (field === 'data_limit_presentacio' || field === 'data_inici_possible' || field === 'data_documentacio');
  }

  private static buildTableFields(translate: TranslateService): Array<[string, string, number]> {
    return [
      ['clau', translate.instant('projectsList.camps.codi'), 15],
      ['titol', translate.instant('projectsList.camps.titol'), 15],
      ['empresa', translate.instant('projectsList.camps.empresa'), 10],
      ['socis', translate.instant('projectsList.camps.socis'), 10],
      ['data_limit_presentacio', translate.instant('projectsList.camps.limitProposta'), 8],
      ['data_documentacio', translate.instant('projectsList.camps.limitRequeriment'), 8],
      ['coordinador_nom', translate.instant('projectsList.camps.coordinador'), 9],
      ['pressupost_licitacio', translate.instant('projectsList.camps.licitacio'), 6],
      ['import_propi', translate.instant('projectsList.camps.propi'), 6],
      ['durada_mesos', translate.instant('projectsList.camps.mesos'), 9]
    ];
  }

  private static buildFilters(translate: TranslateService, listService: ListService): Array<any> {
    const entitat = translate.instant('projectsList.entitatNoms.propostes');
    return [
      {
        inputs: [
          {
            name: 'projectState',
            type: 'checkbox',
            values: [
              { text: translate.instant('projectsList.estatsProposta.enRedaccio'), name: 'stateInProgress', value: false },
              { text: translate.instant('projectsList.estatsProposta.abandonades'), name: 'stateAbandoned', value: false },
              { text: translate.instant('projectsList.estatsProposta.noPresentadesATemps'), name: 'stateNoPresentedtTemps', value: false },
              { text: translate.instant('projectsList.estatsProposta.presentades'), name: 'statePresented', value: false },
              { text: translate.instant('projectsList.estatsProposta.presentadesAmbRequeriments'), name: 'statePresentedReq', value: false },
              { text: translate.instant('projectsList.estatsProposta.noAprovades'), name: 'stateNoAproved', value: false },
              { text: translate.instant('projectsList.estatsProposta.cancellades'), name: 'stateCancelled', value: false },
              { text: translate.instant('projectsList.estatsProposta.aprovadesEnContractacio'), name: 'stateAprovEnCon', value: false },
              { text: translate.instant('projectsList.estatsProposta.contractades'), name: 'stateContracted', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.incidencies', { entitat }), icon: 'report_problem', name: 'incidencies', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.senseImportPropi', { entitat }), icon: 'monetization_on', name: 'importp', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.senseDataIniciPossible', { entitat }), icon: 'picture_in_picture_alt', name: 'datespi', value: false },
              { text: translate.instant('projectsList.coordinadorFiltre'), icon: 'coordinador', name: 'coordinador', value: false }
            ]
          }
        ]
      },
      {
        inputs: [
          {
            name: 'text',
            type: 'text',
            values: [
              { text: translate.instant('projectsList.camps.codi'), name: 'clau', autocomplete: 'codiClauPro', value: listService.getSearchValues('codi') },
              { text: translate.instant('projectsList.camps.titol'), name: 'dataTitol', value: listService.getSearchValues('dataTitol'), type: 'text' },
              { text: translate.instant('projectsList.campsCerca.tematica'), name: 'dataTematica', autocomplete: 'tematica', value: listService.getSearchValues('dataTematica') },
              { text: translate.instant('projectsList.campsCerca.serveis'), name: 'dataServei', autocomplete: 'serveis', value: listService.getSearchValues('dataServei') },
              { text: translate.instant('projectsList.campsCerca.subServeis'), name: 'dataSubservei', autocomplete: 'subserveis', value: listService.getSearchValues('dataSubservei') },
              { text: translate.instant('projectsList.campsCerca.sectors'), name: 'dataSector', autocomplete: 'sectors', value: listService.getSearchValues('dataSector') },
              { text: translate.instant('projectsList.campsCerca.subSectors'), name: 'dataSubsector', autocomplete: 'subsectors', value: listService.getSearchValues('dataSubsector') },
              { text: translate.instant('projectsList.campsCerca.ambitProposta'), name: 'ambitProp', autocomplete: 'ambitProp', value: listService.getSearchValues('ambitPropo') },
              { text: translate.instant('projectsList.camps.empresa'), name: 'dataEmpresa', autocomplete: 'empresa', value: listService.getSearchValues('dataEmpresa') },
              { text: translate.instant('projectsList.campsCerca.client'), name: 'dataClient', autocomplete: 'client', value: listService.getSearchValues('dataClient') },
              { text: translate.instant('projectsList.camps.socis'), name: 'dataSocis', autocomplete: 'soci', value: listService.getSearchValues('dataSocis') },
              { text: translate.instant('projectsList.camps.tecnic'), name: 'dataTecnic', autocomplete: 'tecnicMcrit', value: listService.getSearchValues('dataTecnic') },
              { text: translate.instant('projectsList.campsCerca.cpv'), name: 'dataCPV', autocomplete: 'cpv', value: listService.getSearchValues('dataCPV') }
            ]
          }
        ]
      }
    ];
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, listService: ListService, tokenStorage: TokenStorageService, route: ActivatedRoute, router: Router) {
    super(translate, languageService, listService, tokenStorage, route, router,
      ProposalsListComponent.buildTableFields(translate),
      'proposal',
      [
        {
          cnd_dataEmpresa: ' = ' //totes les condicions van dins l'objecte, no com array!!!
        }
      ],
      ProposalsListComponent.buildFilters(translate, listService),
      ['data_limit_presentacio']
    );
    this.title = translate.instant('projectsList.titols.propostes');
    //route.params.subscribe(params => {if(params.id == 1)this.title = "manifestacions d'interès";});
  };

  ngOnInit() {
    this.watchLangChange(() => {
      this.title = this.translate.instant('projectsList.titols.propostes');
      this.tableFields = ProposalsListComponent.buildTableFields(this.translate);
      this.projectFilters = ProposalsListComponent.buildFilters(this.translate, this.listService);
    });
  }
}

@Component({
  selector: 'mcrit-gestio-proposalspe-list',
  templateUrl: 'projects-list.component.html',
  styleUrls: ['projects-list.component.css']
})
export class ProposalsPeListComponent extends BaseListComponent implements OnInit {
  @Input('elementList') elementList: any;
  @Input('resumeData') resumeData: resumeDataInterface;
  @Output('requestRows') requestRows = new EventEmitter();

  public title: string;

  public isDate(field: string): boolean {
    return (field === 'data_limit_presentacio' || field === 'data_inici_possible' || field === 'data_documentacio');
  }

  private static buildTableFields(translate: TranslateService): Array<[string, string, number]> {
    return [
      ['clau', translate.instant('projectsList.camps.codi'), 15],
      ['titol', translate.instant('projectsList.camps.titol'), 22],
      ['empresa', translate.instant('projectsList.camps.empresa'), 10],
      ['socis', translate.instant('projectsList.camps.socis'), 10],
      ['data_limit_presentacio', translate.instant('projectsList.camps.limit'), 10],
      ['data_inici_possible', translate.instant('projectsList.camps.inici'), 12],
      ['pressupost_licitacio', translate.instant('projectsList.camps.licitacio'), 6],
      ['import_propi', translate.instant('projectsList.camps.propi'), 6],
      ['durada_mesos', translate.instant('projectsList.camps.mesos'), 9]
    ];
  }

  private static buildFilters(translate: TranslateService, listService: ListService): Array<any> {
    const entitat = translate.instant('projectsList.entitatNoms.manifestacions');
    return [
      {
        inputs: [
          {
            name: 'projectState',
            type: 'checkbox',
            values: [
              { text: translate.instant('projectsList.estatsProposta.enRedaccio'), name: 'stateInProgress', value: false },
              { text: translate.instant('projectsList.estatsProposta.abandonades'), name: 'stateAbandoned', value: false },
              { text: translate.instant('projectsList.estatsProposta.noPresentadesATemps'), name: 'stateNoPresentedtTemps', value: false },
              { text: translate.instant('projectsList.estatsProposta.presentades'), name: 'statePresented', value: false },
              { text: translate.instant('projectsList.estatsProposta.presentadesAmbRequeriments'), name: 'statePresentedReq', value: false },
              { text: translate.instant('projectsList.estatsProposta.noAprovades'), name: 'stateNotAccepted', value: false },
              { text: translate.instant('projectsList.estatsProposta.cancellades'), name: 'stateCancelled', value: false },
              { text: translate.instant('projectsList.estatsProposta.aprovades'), name: 'stateAproved', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.incidencies', { entitat }), icon: 'report_problem', name: 'incidencies', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.senseImportPropi', { entitat }), icon: 'monetization_on', name: 'importp', value: false },
              { text: translate.instant('projectsList.filtresErrorsEntitat.senseDataIniciPossible', { entitat }), icon: 'picture_in_picture_alt', name: 'datespi', value: false },
              { text: translate.instant('projectsList.coordinadorFiltre'), icon: 'coordinador', name: 'coordinador', value: false }
            ]
          }
        ]
      },
      {
        inputs: [
          {
            name: 'text',
            type: 'text',
            values: [
              { text: translate.instant('projectsList.camps.codi'), name: 'clau', autocomplete: 'codiClauPre', value: listService.getSearchValues('codi') },
              { text: translate.instant('projectsList.camps.titol'), name: 'dataTitol', value: listService.getSearchValues('dataTitol'), type: 'text' },
              { text: translate.instant('projectsList.campsCerca.tematica'), name: 'dataTematica', autocomplete: 'tematica', value: listService.getSearchValues('dataTematica') },
              { text: translate.instant('projectsList.camps.empresa'), name: 'dataEmpresa', autocomplete: 'empresa', value: listService.getSearchValues('dataEmpresa') },
              { text: translate.instant('projectsList.campsCerca.client'), name: 'dataClient', autocomplete: 'client', value: listService.getSearchValues('dataClient') },
              { text: translate.instant('projectsList.camps.socis'), name: 'dataSocis', autocomplete: 'soci', value: listService.getSearchValues('dataSocis') },
              { text: translate.instant('projectsList.camps.tecnic'), name: 'dataTecnic', autocomplete: 'tecnicMcrit', value: listService.getSearchValues('dataTecnic') }
            ]
          }
        ]
      }
    ];
  }

  constructor(public translate: TranslateService, public languageService: LanguageService, listService: ListService, tokenStorage: TokenStorageService, route: ActivatedRoute, router: Router) {
    super(translate, languageService, listService, tokenStorage, route, router,
      ProposalsPeListComponent.buildTableFields(translate),
      'proposalpe',
      [
        {
          cnd_dataEmpresa: ' = ' //totes les condicions van dins l'objecte, no com array!!!
        }
      ],
      ProposalsPeListComponent.buildFilters(translate, listService),
      ['data_limit_presentacio']
    );
    this.title = translate.instant('projectsList.titols.manifestacions');
  };

  ngOnInit() {
    this.watchLangChange(() => {
      this.title = this.translate.instant('projectsList.titols.manifestacions');
      this.tableFields = ProposalsPeListComponent.buildTableFields(this.translate);
      this.projectFilters = ProposalsPeListComponent.buildFilters(this.translate, this.listService);
    });
  }
}
